/**
 * asaasOnboarding — regras PURAS do formulário de criação da subconta Asaas.
 *
 * Módulo separado do I/O (`useAsaasAccount.ts`) pela mesma razão de sempre
 * neste repo: validação e montagem de payload são regra de NEGÓCIO, e regra
 * de negócio precisa de teste que não dependa de rede nem de mock de hook.
 *
 * A UNIDADE deste formulário é a SUBCONTA — não a empresa. O titular da conta
 * de recebimento pode ser diferente do cadastro em `companies` (ex.: MEI cujo
 * CPF pessoal é o titular Asaas, com CNPJ na razão social do app), então os
 * campos são digitados aqui, não herdados silenciosamente de `companies` —
 * herdar tornaria uma mudança no cadastro da empresa uma mudança na conta
 * bancária, sem o usuário perceber.
 */
import { parseBRLInput } from '@/lib/currencyInput';
import { isValidDocument, onlyDigits } from '@/lib/documentUtils';

export type AsaasCompanyType = 'MEI' | 'LIMITED' | 'INDIVIDUAL' | 'ASSOCIATION';

export interface AsaasOnboardFormValues {
  name: string;
  email: string;
  cpfCnpj: string;
  mobilePhone: string;
  /** Só para CPF (11 dígitos). */
  birthDate: string;
  /** Só para CNPJ (14 dígitos). */
  companyType: AsaasCompanyType;
  /**
   * Faturamento/renda mensal do titular, em TEXTO MASCARADO pt-BR ("1.500,00").
   *
   * É o único campo do formulário que o Asaas exige e que não é identidade nem
   * endereço — sem ele o `POST /v3/accounts` responde 400 (`invalid_object`,
   * que chega como texto genérico e não diz qual campo faltou). Guardado como
   * texto porque quem digita é a MÁSCARA (`maskBRLInput`): não existe parser
   * que resolva a ambiguidade do ponto em pt-BR (#157/#209), e um número aqui
   * obrigaria a converter a cada tecla.
   */
  incomeValue: string;
  address: string;
  addressNumber: string;
  complement: string;
  province: string;
  postalCode: string;
}

export const EMPTY_ASAAS_ONBOARD_FORM: AsaasOnboardFormValues = {
  name: '',
  email: '',
  cpfCnpj: '',
  mobilePhone: '',
  birthDate: '',
  companyType: 'MEI',
  incomeValue: '',
  address: '',
  addressNumber: '',
  complement: '',
  province: '',
  postalCode: '',
};

/** 11 dígitos = CPF (a subconta é de pessoa física); 14 = CNPJ. */
export function asaasDocumentIsCnpj(cpfCnpj: string): boolean {
  return onlyDigits(cpfCnpj).length === 14;
}

export interface AsaasOnboardValidation {
  ok: boolean;
  /** Uma mensagem por campo com problema — vazio quando `ok`. */
  errors: Partial<Record<keyof AsaasOnboardFormValues, string>>;
}

/**
 * Valida o formulário ANTES de chamar a edge function.
 *
 * Por que validar no cliente além do servidor: o Asaas cobra por tentativa de
 * criação de subconta em termos de janela regulatória (#165c) — cada POST
 * malsucedido não conta contra o limite de 10, mas ainda assim é uma chamada
 * evitável a um provedor externo por um erro que o próprio navegador já sabia.
 */
export function validateAsaasOnboardForm(values: AsaasOnboardFormValues): AsaasOnboardValidation {
  const errors: AsaasOnboardValidation['errors'] = {};

  if (!values.name.trim()) errors.name = 'Informe o nome do titular.';
  if (!values.email.trim() || !values.email.includes('@')) errors.email = 'Informe um e-mail válido.';

  const digits = onlyDigits(values.cpfCnpj);
  if (digits.length !== 11 && digits.length !== 14) {
    errors.cpfCnpj = 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).';
  } else if (isValidDocument(digits) === false) {
    errors.cpfCnpj = digits.length === 11 ? 'CPF inválido.' : 'CNPJ inválido.';
  } else if (digits.length === 11 && !values.birthDate) {
    // O Asaas exige data de nascimento para CPF — sem ela o POST recusa com
    // `invalid_object`, que chega como texto genérico e não diz qual campo.
    errors.birthDate = 'Informe a data de nascimento do titular.';
  }

  if (onlyDigits(values.mobilePhone).length < 10) errors.mobilePhone = 'Informe um celular com DDD.';
  // Obrigatório na API do Asaas, e ZERO não é resposta: uma conta de pagamento
  // com faturamento declarado zero é recusada na análise. Ausência e zero caem
  // na mesma mensagem de propósito — as duas exigem o mesmo gesto de quem
  // preenche, e distinguir só acrescentaria texto sem acrescentar ação.
  if (parseBRLInput(values.incomeValue) <= 0) {
    errors.incomeValue = 'Informe o faturamento ou renda mensal do titular.';
  }
  if (onlyDigits(values.postalCode).length !== 8) errors.postalCode = 'Informe um CEP válido.';
  if (!values.address.trim()) errors.address = 'Informe o logradouro.';
  if (!values.addressNumber.trim()) errors.addressNumber = 'Informe o número.';
  if (!values.province.trim()) errors.province = 'Informe o bairro.';

  return { ok: Object.keys(errors).length === 0, errors };
}

/** O corpo que `asaas-onboard` espera, já sem máscara nos dígitos. */
export function buildAsaasOnboardPayload(
  companyId: string,
  values: AsaasOnboardFormValues,
): Record<string, unknown> {
  const cpfCnpj = onlyDigits(values.cpfCnpj);
  const isCnpj = cpfCnpj.length === 14;
  return {
    companyId,
    name: values.name.trim(),
    email: values.email.trim(),
    cpfCnpj,
    mobilePhone: onlyDigits(values.mobilePhone),
    // Só o campo do TIPO de documento faz sentido enviar — o outro o Asaas
    // ignora, mas mandar os dois juntos convida a divergência (#73/#166: a
    // fonte única é o comprimento do documento, não uma segunda escolha).
    birthDate: isCnpj ? undefined : values.birthDate || undefined,
    companyType: isCnpj ? values.companyType : undefined,
    // O Asaas espera NÚMERO; o campo guarda o texto mascarado. `parseBRLInput`
    // é o parser de DINHEIRO do repo (pontos são milhar, vírgula é decimal) —
    // parsear à mão aqui é o defeito D7 (#151a) num campo que decide análise
    // cadastral em vez de um lançamento.
    incomeValue: parseBRLInput(values.incomeValue),
    address: values.address.trim(),
    addressNumber: values.addressNumber.trim(),
    complement: values.complement.trim() || undefined,
    province: values.province.trim(),
    postalCode: onlyDigits(values.postalCode),
  };
}

// ─── Documentos do checklist de onboarding ─────────────────────────────────

export type AsaasDocumentGroupStatus = 'NOT_SENT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'IGNORED';

export interface AsaasDocumentGroup {
  id: string;
  status: AsaasDocumentGroupStatus;
  type: string;
  title: string;
  description: string;
  onboardingUrl: string | null;
}

export const ASAAS_DOCUMENT_STATUS_LABELS: Record<AsaasDocumentGroupStatus, string> = {
  NOT_SENT: 'Não enviado',
  PENDING: 'Em análise',
  APPROVED: 'Aprovado',
  REJECTED: 'Recusado — reenvie',
  IGNORED: 'Não necessário',
};

/**
 * A conta pode ser consultada logo após a criação, quando o Asaas ainda
 * está validando o cadastro na Receita Federal — a documentação oficial
 * (`onboarding-e-envio-de-documentos-via-link`) recomenda esperar 15s antes
 * da primeira consulta a `/myAccount/documents`, senão ela responde com
 * documentos que na verdade ainda não são obrigatórios.
 */
export const ASAAS_DOCUMENTS_QUERY_DELAY_MS = 15_000;

export function asaasDocumentsQueryReady(accountCreatedAt: string, now: number): boolean {
  const createdAtMs = new Date(accountCreatedAt).getTime();
  if (Number.isNaN(createdAtMs)) return true;
  return now - createdAtMs >= ASAAS_DOCUMENTS_QUERY_DELAY_MS;
}
